(() => {
  const init = (root) => {
  if (!root || !root.matches('[data-tan-event-brief]') || root.dataset.eventBriefReady) return;

  root.dataset.eventBriefReady = 'true';

  const i18nNode = root.querySelector('[data-event-brief-i18n]');
  let i18n = {};
  try {
    i18n = JSON.parse(i18nNode?.textContent || '{}');
  } catch (_) {
    i18n = {};
  }

  const text = (key) => String(i18n[key] || '');
  const interpolate = (key, values) => Object.entries(values).reduce(
    (result, [name, value]) => result.split(`__${name.toUpperCase()}__`).join(String(value)),
    text(key),
  );

  const groups = ['event', 'role', 'environment'];
  const choices = Array.from(root.querySelectorAll('[data-event-choice]'));
  const dateInput = root.querySelector('[data-event-date]');
  const dateCard = root.querySelector('[data-event-date-card]');
  const dateBadge = root.querySelector('[data-event-date-badge]');
  const dateHeading = root.querySelector('[data-event-date-heading]');
  const dateCopy = root.querySelector('[data-event-date-copy]');
  const dateDays = root.querySelector('[data-event-date-days]');
  const resultHeading = root.querySelector('[data-event-result-heading]');
  const resultCopy = root.querySelector('[data-event-result-copy]');
  const secondary = root.querySelector('[data-event-secondary]');
  const copyField = root.querySelector('[data-event-copy-text]');
  const copyButton = root.querySelector('[data-event-copy]');

  const briefLabels = {
    title: text('brief_title'),
    date: text('brief_date'),
    type: text('brief_type'),
    role: text('brief_role'),
    environment: text('brief_environment'),
    eventPriorities: text('brief_event_priorities'),
    rolePriorities: text('brief_role_priorities'),
    environmentPriorities: text('brief_environment_priorities'),
  };

  const urgentDays = Number(root.dataset.urgentDays) || 9;
  const sensitiveDays = Number(root.dataset.sensitiveDays) || 20;
  const planningDays = Number(root.dataset.planningDays) || 34;

  const localIsoDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const startOfToday = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  };

  const selectedFor = (group) =>
    choices.find((choice) => choice.dataset.choiceGroup === group && choice.classList.contains('is-active'));

  const payloadFor = (choice) =>
    Array.from(root.querySelectorAll('[data-event-payload]')).find(
      (payload) => payload.dataset.eventPayload === choice.dataset.choiceId,
    );

  const checklistText = (choice) => {
    const payload = payloadFor(choice);
    if (!payload) return '';
    return Array.from(payload.content.querySelectorAll('li'))
      .map((item) => item.textContent.trim())
      .filter(Boolean)
      .join('; ');
  };

  const dateState = () => {
    if (!dateInput || !dateInput.value) {
      return { status: 'empty', label: text('not_added'), days: null };
    }

    const eventDate = new Date(`${dateInput.value}T12:00:00`);
    if (Number.isNaN(eventDate.getTime())) return { status: 'empty', label: text('not_added'), days: null };

    const days = Math.ceil((eventDate.getTime() - startOfToday().getTime()) / 86400000);
    if (days < 0) return { status: 'past', label: text('status_past'), days };
    if (days <= urgentDays) return { status: 'urgent', label: text('status_urgent'), days };
    if (days <= sensitiveDays) return { status: 'sensitive', label: text('status_sensitive'), days };
    if (days <= planningDays) return { status: 'planning', label: text('status_planning'), days };
    return { status: 'strong', label: text('status_strong'), days };
  };

  const updateDate = () => {
    if (!dateCard || !dateBadge || !dateHeading || !dateCopy) return;
    const state = dateState();
    dateCard.dataset.dateStatus = state.status;

    const states = {
      empty: {
        badge: text('empty_badge'),
        heading: text('empty_heading'),
        copy: text('empty_copy'),
      },
      past: {
        badge: text('past_badge'),
        heading: text('past_heading'),
        copy: text('past_copy'),
      },
      urgent: {
        badge: text('urgent_badge'),
        heading: text('urgent_heading'),
        copy: text('urgent_copy'),
      },
      sensitive: {
        badge: text('sensitive_badge'),
        heading: text('sensitive_heading'),
        copy: text('sensitive_copy'),
      },
      planning: {
        badge: text('planning_badge'),
        heading: text('planning_heading'),
        copy: text('planning_copy'),
      },
      strong: {
        badge: text('strong_badge'),
        heading: text('strong_heading'),
        copy: text('strong_copy'),
      },
    };

    const content = states[state.status] || states.empty;
    dateBadge.textContent = content.badge;
    dateHeading.textContent = content.heading;
    dateCopy.textContent = content.copy;

    if (dateDays) {
      dateDays.hidden = state.days === null || state.days < 0;
      dateDays.textContent = interpolate(state.days === 1 ? 'day_one' : 'day_other', { count: state.days });
    }
  };

  const updateResult = () => {
    const selected = Object.fromEntries(groups.map((group) => [group, selectedFor(group)]));
    if (groups.some((group) => !selected[group])) return;

    choices.forEach((choice) => {
      const isSelected = selected[choice.dataset.choiceGroup] === choice;
      choice.classList.toggle('is-active', isSelected);
      choice.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
    });

    if (resultHeading) {
      resultHeading.textContent = groups.map((group) => selected[group].dataset.choiceTitle || '').join(' · ');
    }
    if (resultCopy) {
      resultCopy.textContent = groups.map((group) => selected[group].dataset.choiceNote || '').join(' ');
    }

    groups.forEach((group) => {
      const list = root.querySelector(`[data-event-list="${group}"]`);
      const payload = payloadFor(selected[group]);
      const content = payload && payload.content.querySelector('[data-event-checklist]');
      if (list) list.innerHTML = content ? content.innerHTML : '';
    });

    if (secondary) {
      const environment = selected.environment;
      const label = environment.dataset.guideLabel || '';
      const link = environment.dataset.guideLink || '';
      secondary.hidden = !label || !link;
      if (label) secondary.textContent = label;
      if (link) secondary.setAttribute('href', link);
    }

    if (copyField) {
      const state = dateState();
      const eventDate = dateInput && dateInput.value ? dateInput.value : text('not_added');
      copyField.value = [
        briefLabels.title,
        `${briefLabels.date}: ${eventDate}`,
        interpolate('timing_status', { status: state.label }),
        `${briefLabels.type}: ${selected.event.dataset.choiceTitle || ''}`,
        `${briefLabels.role}: ${selected.role.dataset.choiceTitle || ''}`,
        `${briefLabels.environment}: ${selected.environment.dataset.choiceTitle || ''}`,
        `${briefLabels.eventPriorities}: ${checklistText(selected.event)}`,
        `${briefLabels.rolePriorities}: ${checklistText(selected.role)}`,
        `${briefLabels.environmentPriorities}: ${checklistText(selected.environment)}`,
        text('review_requested'),
      ].join('\n');
    }
  };

  const selectChoice = (choice) => {
    if (!choice) return;
    choices
      .filter((candidate) => candidate.dataset.choiceGroup === choice.dataset.choiceGroup)
      .forEach((candidate) => candidate.classList.toggle('is-active', candidate === choice));
    updateResult();
  };

  const copyBrief = async () => {
    if (!copyField || !copyButton) return;
    const originalLabel = copyButton.dataset.copyLabel || copyButton.textContent;
    const copiedLabel = copyButton.dataset.copiedLabel || text('copied');

    let copied = false;
    try {
      await navigator.clipboard.writeText(copyField.value);
      copied = true;
    } catch (_error) {
      copyField.focus();
      copyField.select();
      try {
        copied = document.execCommand('copy');
      } catch (_) {
        copied = false;
      }
    }

    const status = copied ? copiedLabel : text('copy_error');
    copyButton.textContent = status;
    copyButton.setAttribute('aria-label', status);
    window.setTimeout(() => {
      copyButton.textContent = originalLabel;
      copyButton.setAttribute('aria-label', originalLabel);
    }, 1800);
  };

  choices.forEach((choice) => choice.addEventListener('click', () => selectChoice(choice)));
  if (dateInput) {
    dateInput.min = localIsoDate(startOfToday());
    dateInput.addEventListener('change', () => {
      updateDate();
      updateResult();
    });
  }
  if (copyButton) copyButton.addEventListener('click', copyBrief);

  updateDate();
  updateResult();
  };

  const initAll = (scope = document) => {
    if (scope.matches?.('[data-tan-event-brief]')) init(scope);
    scope.querySelectorAll?.('[data-tan-event-brief]').forEach(init);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
  else initAll();
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
